import { ModalLoading, ModalContent, Button } from '@clearcover/atlas';
import { Formik } from 'formik';
import React, { FC } from 'react';
import { SelectInput } from 'Shared/components/FormInput/Variants';
import { FormInput } from 'Shared/components/FormInput';
import * as Yup from 'yup';
import { GetPortalPolicyVehicleInspectionsResponseV2 } from '@clearcover/core-app-types';
import { AcceptedStatusType } from '../types';
import styles from './styles.module.css';

export type VehicleSetupRequestFormValues = {
  vehicleId: string;
  reason: string;
};

const VEHICLE_SETUP_REQUEST_SCHEMA = Yup.object().shape({
  vehicleId: Yup.string().required('Vehicle selection is required'),
  reason: Yup.string().required('Setup reason is required'),
});

export type VehicleSetupRequestFormType = {
  close: () => void;
  vehicleOptions: { [key: string]: string };
  vehicleInspectionRequests:
    | GetPortalPolicyVehicleInspectionsResponseV2
    | undefined;
  changeStatus: (
    requestId: string,
    status: AcceptedStatusType,
    event?: string
  ) => Promise<void>;
};

export const VehicleSetupRequestForm: FC<VehicleSetupRequestFormType> = ({
  close,
  vehicleOptions,
  vehicleInspectionRequests,
  changeStatus,
}) => {
  const hasVehicleOptions = Object.keys(vehicleOptions).length > 0;

  if (!hasVehicleOptions) {
    return <ModalLoading data-testid="vehicle-setup-request-modal-loading" />;
  }

  const INITIAL_FORM_VALUES: VehicleSetupRequestFormValues = {
    vehicleId: '',
    reason: '',
  };

  return (
    <Formik
      initialValues={INITIAL_FORM_VALUES}
      onSubmit={async (values: VehicleSetupRequestFormValues) => {
        const status: AcceptedStatusType = 'OPEN';
        const event =
          values.reason === 'coverage'
            ? 'COVERAGE_ADDITION_PHYSICAL_DAMAGE'
            : undefined;

        // Find the requestId for the selected vehicle
        const vehicleRequest = vehicleInspectionRequests?.find(
          request => request.vehicleId === values.vehicleId
        );

        if (vehicleRequest?.requestId) {
          try {
            await changeStatus(vehicleRequest.requestId, status, event);
            close();
          } catch (error) {
            console.error('Error changing status');
          }
        }
      }}
      validationSchema={VEHICLE_SETUP_REQUEST_SCHEMA}
      validateOnMount
    >
      {({ values, setFieldValue, isValid, submitForm, isSubmitting }) => {
        return (
          <div className={styles.container}>
            <ModalContent className={styles.content}>
              <h2>
                Please select a set up reason for new vehicle set up request
              </h2>
              <div style={{ marginBottom: '24px' }}>
                <SelectInput
                  label="Which vehicle needs new vehicle set up request"
                  value={values.vehicleId}
                  onChange={(value: string) =>
                    setFieldValue('vehicleId', value)
                  }
                  options={vehicleOptions}
                  placeholder="Choose a vehicle"
                  name="vehicleId"
                  variant="select"
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div className={styles.radioContainer}>
                  <FormInput
                    variant="radio"
                    name="reason"
                    label="Retake a poor quality video"
                    value="retake"
                    checked={values.reason === 'retake'}
                    onChecked={() => {
                      setFieldValue('reason', 'retake');
                    }}
                    className={styles.radio}
                  />
                </div>
                <div className={styles.radioContainer}>
                  <FormInput
                    variant="radio"
                    name="reason"
                    label="Comprehensive/Collision coverage request"
                    value="coverage"
                    checked={values.reason === 'coverage'}
                    onChecked={() => {
                      setFieldValue('reason', 'coverage');
                    }}
                    className={styles.radio}
                  />
                </div>
              </div>
              <div className={styles.buttonGroup}>
                <Button
                  variant="danger-secondary"
                  isSubmitting={isSubmitting}
                  onClick={close}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={submitForm}
                  disabled={!isValid || isSubmitting}
                >
                  Save
                </Button>
              </div>
            </ModalContent>
          </div>
        );
      }}
    </Formik>
  );
};
